/**
 * Zod schema for the §B account-setup status wire shape.
 *
 * Lives in auth-core, next to the AccountSetupStatus type it validates, because BOTH apps mount the same
 * interstitial: the portal (end users) and the admin console (admins enrolling). A schema copied into one
 * app protects one app -- and the app it would have missed is the admin console, whose trapped user is
 * the WORST case on the platform: "contact your administrator" is a dead end when you ARE the
 * administrator. CLAUDE.md's rule (horizontal code goes through a workspace package) is load-bearing
 * here, not stylistic.
 *
 * # Why a parse and not a cast
 *
 * Both apps fetched status as a bare `apiFetch<AccountSetupStatus>` -- TypeScript believed the shape;
 * nothing checked it. That matters precisely here: the interstitial decides whether to show a blocked
 * user the METHOD MENU or an ESCALATION SCREEN by reading `verdict`. A user with no portable credential
 * cannot clear the floor with anything on the menu; `verdict: "unsatisfiable"` is the server saying stop
 * offering and start escalating.
 *
 * Under a cast, a server that stops sending `verdict` yields undefined, the interstitial silently falls
 * back to its old "is the menu empty?" heuristic, and every trapped user quietly returns to the futile
 * loop. Nothing fails. Nobody notices. That silent degradation is this codebase's signature defect, and a
 * parse is what makes it loud.
 */

import { z } from "zod";

/**
 * The methods the setup interstitial can install.
 *
 * `passkey` LEADS, and it is the only one of the four that survives losing the device. The other three
 * are one tier below that, which was the whole D3 trap: a user with no portable credential could add
 * every method on this list and remain below a `standard` floor forever.
 *
 * NOTE this is a z.enum while `verdict` next to it is a plain string, and the asymmetry is deliberate. An
 * unknown VERDICT must not brick the interstitial (it falls through to the safe branch, which is the
 * behaviour we had before the field existed). An unknown METHOD is different: the interstitial has to
 * RENDER it, and it cannot render a method it has no card for. Failing parse is the honest outcome —
 * silently dropping an unknown method would hide the one option a newer server is trying to offer, which
 * on this screen could be the only option that helps.
 *
 * That makes the deploy order load-bearing: this schema ships BEFORE a server that emits "passkey".
 */
export const accountSetupMethodSchema = z.enum([
  "passkey",
  "recovery_email",
  "recovery_codes",
  "totp",
]);

/**
 * GET /me/account-setup/status.
 *
 * `verdict` is REQUIRED here even though the TS interface marks it optional (fixtures need not restate
 * it). Required on the wire is the point: a backend that drops the field fails parse loudly rather than
 * stranding users quietly.
 *
 * It is a plain string, not a z.enum: a NEW verdict from a newer server must not fail-parse the whole
 * status read and brick the interstitial. Unknown verdicts fall through to the safe branch (show the
 * menu), which is the behaviour we had before the field existed.
 */
export const accountSetupStatusSchema = z.object({
  complete: z.boolean(),
  available: z.array(accountSetupMethodSchema),
  verdict: z.string().min(1).max(32),
  /**
   * Why `passkey` is missing from `available`, when it is missing for a reason the user can act on.
   *
   * Optional and a plain string for the same reason `verdict` is: a newer server with a new reason must
   * not fail-parse the whole read. Unknown reasons render the generic explanation.
   *
   * Absent means the route is offered. It does NOT mean "no reason" — a client that ignores this shows a
   * user who was just told she needs a credential surviving device loss a screen with no passkey button
   * and no explanation, which is the futile-option trap rebuilt by omission.
   */
  passkey_blocked_reason: z.string().max(64).optional(),
});

export type AccountSetupStatusWire = z.infer<typeof accountSetupStatusSchema>;
